import { create } from 'zustand';
import { ConnectionQuality } from '@/models/retry-state.model';
import { useToastStore } from '@/stores/toast.store';

/**
 * @fileoverview Network status store (Zustand)
 * @description
 * React/Zustand port of the Angular `NetworkStatusService`. Tracks online /
 * offline status and connection quality. Signal names, method names, config
 * values, and behaviour are reproduced 1:1.
 *
 * Adaptation notes:
 * - The Angular service registered window/document listeners and started
 *   periodic checks in its constructor. In React the side-effecting setup is
 *   moved into an explicit `init()` (idempotent) so it is only wired once on
 *   app mount, satisfying the "no side effects in module load" rule.
 * - The injected `Toast` service is replaced by the `useToastStore` singleton,
 *   accessed via `getState()` (decoupled, same `showToast` signature).
 */

/** Network status change event (mirrors Angular NetworkStatusChangeEvent). */
export interface NetworkStatusChangeEvent {
  isOnline: boolean;
  wasOnline: boolean;
  quality: ConnectionQuality;
  timestamp: number;
}

interface NetworkConfig {
  pingInterval: number;
  pingTimeout: number;
  showNotifications: boolean;
}

interface NetworkStatusState {
  /** Current online status */
  isOnlineState: boolean;
  /** Connection quality estimate */
  connectionQuality: ConnectionQuality;
  /** Last time connection was checked */
  lastCheckTime: number;
  /** Average latency in milliseconds */
  avgLatency: number | null;
  /** Whether currently checking connection */
  isChecking: boolean;

  /** Method accessor matching Angular's `isOnline()` signal callable. */
  isOnline: () => boolean;

  checkConnection: () => Promise<boolean>;
  stopPeriodicChecks: () => void;
  onStatusChange: (callback: (event: NetworkStatusChangeEvent) => void) => () => void;
  getStatusText: () => string;
  getStatusColorClass: () => string;
  getStatusIcon: () => string;
  setNotifications: (enabled: boolean) => void;
  /** Register listeners + start periodic checks (was the Angular constructor). */
  init: () => void;
  /** Cleanup (was ngOnDestroy). */
  destroy: () => void;
}

const config: NetworkConfig = {
  pingInterval: 30000, // 30 seconds
  pingTimeout: 5000, // 5 seconds
  showNotifications: true,
};

let statusChangeCallbacks: Array<(event: NetworkStatusChangeEvent) => void> = [];
let pingIntervalId: ReturnType<typeof setInterval> | null = null;
let initialized = false;
let onlineHandler: (() => void) | null = null;
let offlineHandler: (() => void) | null = null;
let visibilityHandler: (() => void) | null = null;
let notificationsWired = false;

/** Determine connection quality from latency (was private determineQuality). */
function determineQuality(latency: number): ConnectionQuality {
  if (latency < 100) return ConnectionQuality.EXCELLENT;
  if (latency < 300) return ConnectionQuality.GOOD;
  if (latency < 1000) return ConnectionQuality.FAIR;
  return ConnectionQuality.POOR;
}

export const useNetworkStatusStore = create<NetworkStatusState>((set, get) => {
  /** Notify all listeners of status change (was private notifyStatusChange). */
  const notifyStatusChange = (event: NetworkStatusChangeEvent): void => {
    statusChangeCallbacks.forEach((callback) => {
      try {
        callback(event);
      } catch (error) {
        console.error('Error in network status callback:', error);
      }
    });
  };

  /** Update average latency (was private updateLatency). */
  const updateLatency = (newLatency: number): void => {
    const currentAvg = get().avgLatency;
    if (currentAvg === null) {
      set({ avgLatency: newLatency });
    } else {
      const smoothedLatency = currentAvg * 0.7 + newLatency * 0.3;
      set({ avgLatency: Math.round(smoothedLatency) });
    }
  };

  const handleOnlineEvent = (): void => {
    const wasOnline = get().isOnlineState;
    set({ isOnlineState: true });
    // Check connection quality immediately
    void get().checkConnection();
    notifyStatusChange({
      isOnline: true,
      wasOnline,
      quality: get().connectionQuality,
      timestamp: Date.now(),
    });
  };

  const handleOfflineEvent = (): void => {
    const wasOnline = get().isOnlineState;
    set({ isOnlineState: false, connectionQuality: ConnectionQuality.UNKNOWN });
    notifyStatusChange({
      isOnline: false,
      wasOnline,
      quality: ConnectionQuality.UNKNOWN,
      timestamp: Date.now(),
    });
  };

  return {
    isOnlineState: typeof navigator !== 'undefined' ? navigator.onLine : true,
    connectionQuality: ConnectionQuality.UNKNOWN,
    lastCheckTime: Date.now(),
    avgLatency: null,
    isChecking: false,

    isOnline: () => get().isOnlineState,

    checkConnection: async () => {
      if (get().isChecking) return get().isOnline();

      set({ isChecking: true });

      try {
        const startTime = performance.now();

        await fetch('https://www.google.com/favicon.ico', {
          method: 'HEAD',
          mode: 'no-cors',
          cache: 'no-cache',
          signal: AbortSignal.timeout(config.pingTimeout),
        });

        const endTime = performance.now();
        const latency = endTime - startTime;

        updateLatency(latency);

        const quality = determineQuality(latency);
        set({ connectionQuality: quality });

        const wasOnline = get().isOnlineState;
        set({ isOnlineState: true });

        if (!wasOnline) {
          notifyStatusChange({
            isOnline: true,
            wasOnline,
            quality,
            timestamp: Date.now(),
          });
        }

        set({ lastCheckTime: Date.now() });
        return true;
      } catch {
        const wasOnline = get().isOnlineState;
        set({ isOnlineState: false, connectionQuality: ConnectionQuality.UNKNOWN });

        if (wasOnline) {
          notifyStatusChange({
            isOnline: false,
            wasOnline,
            quality: ConnectionQuality.UNKNOWN,
            timestamp: Date.now(),
          });
        }

        set({ lastCheckTime: Date.now() });
        return false;
      } finally {
        set({ isChecking: false });
      }
    },

    stopPeriodicChecks: () => {
      if (pingIntervalId) {
        clearInterval(pingIntervalId);
        pingIntervalId = null;
      }
    },

    onStatusChange: (callback) => {
      statusChangeCallbacks.push(callback);
      return () => {
        const index = statusChangeCallbacks.indexOf(callback);
        if (index > -1) {
          statusChangeCallbacks.splice(index, 1);
        }
      };
    },

    getStatusText: () => {
      if (!get().isOnline()) return 'Offline';
      switch (get().connectionQuality) {
        case ConnectionQuality.EXCELLENT:
          return 'Online (Excellent)';
        case ConnectionQuality.GOOD:
          return 'Online (Good)';
        case ConnectionQuality.FAIR:
          return 'Online (Fair)';
        case ConnectionQuality.POOR:
          return 'Online (Poor)';
        default:
          return 'Online';
      }
    },

    getStatusColorClass: () => {
      if (!get().isOnline()) return 'text-error-600';
      switch (get().connectionQuality) {
        case ConnectionQuality.EXCELLENT:
        case ConnectionQuality.GOOD:
          return 'text-success-600';
        case ConnectionQuality.FAIR:
          return 'text-warning-600';
        case ConnectionQuality.POOR:
          return 'text-error-600';
        default:
          return 'text-neutral-500';
      }
    },

    getStatusIcon: () => {
      if (!get().isOnline()) return 'wifi-off';
      switch (get().connectionQuality) {
        case ConnectionQuality.EXCELLENT:
        case ConnectionQuality.GOOD:
          return 'wifi';
        case ConnectionQuality.FAIR:
          return 'wifi-low';
        case ConnectionQuality.POOR:
          return 'wifi-zero';
        default:
          return 'wifi';
      }
    },

    setNotifications: (enabled) => {
      config.showNotifications = enabled;
    },

    init: () => {
      if (initialized) return;
      initialized = true;

      // initializeListeners()
      onlineHandler = () => handleOnlineEvent();
      offlineHandler = () => handleOfflineEvent();
      visibilityHandler = () => {
        if (!document.hidden) {
          void get().checkConnection();
        }
      };
      window.addEventListener('online', onlineHandler);
      window.addEventListener('offline', offlineHandler);
      document.addEventListener('visibilitychange', visibilityHandler);

      // setupNotifications()
      if (!notificationsWired) {
        notificationsWired = true;
        let lastNotificationTime = 0;
        const notificationCooldown = 5000; // 5 seconds
        get().onStatusChange((event) => {
          if (!config.showNotifications) return;
          const now = Date.now();
          if (now - lastNotificationTime < notificationCooldown) return;

          if (event.isOnline && !event.wasOnline) {
            useToastStore.getState().showToast('Connection restored', 'success', 3000);
            lastNotificationTime = now;
          } else if (!event.isOnline && event.wasOnline) {
            useToastStore
              .getState()
              .showToast('You are offline. Some features may be limited.', 'warning', 5000);
            lastNotificationTime = now;
          }
        });
      }

      // startPeriodicChecks()
      void get().checkConnection();
      pingIntervalId = setInterval(() => {
        if (document.visibilityState === 'visible') {
          void get().checkConnection();
        }
      }, config.pingInterval);
    },

    destroy: () => {
      get().stopPeriodicChecks();
      if (onlineHandler) window.removeEventListener('online', onlineHandler);
      if (offlineHandler) window.removeEventListener('offline', offlineHandler);
      if (visibilityHandler) document.removeEventListener('visibilitychange', visibilityHandler);
      onlineHandler = null;
      offlineHandler = null;
      visibilityHandler = null;
      statusChangeCallbacks = [];
      initialized = false;
    },
  };
});
